import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Refresh, ShieldOutlined } from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const initialFilters = () => {
  const end = new Date();
  const start = new Date(end); start.setUTCDate(start.getUTCDate() - 7);
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10), outcome: '' };
};
const integrityLabels = { verified: 'Signature verified', invalid: 'Integrity check failed', 'key-unavailable': 'Verification key unavailable' };
const integrityColor = value => value === 'verified' ? 'success' : value === 'invalid' ? 'error' : 'warning';
const timestamp = value => {
  const date = new Date(value);
  return value && Number.isFinite(date.getTime()) ? date.toLocaleString() : 'Unavailable';
};
const short = value => value ? `${value.slice(0, 12)}…` : 'Unavailable';

export default function SecurityAudit() {
  const { user } = useAuth();
  const allowed = user?.role === 'admin';
  const [draft, setDraft] = useState(initialFilters);
  const [filters, setFilters] = useState(draft);
  const [cursor, setCursor] = useState(null);
  const [previous, setPrevious] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [events, setEvents] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterError, setFilterError] = useState('');
  const [selected, setSelected] = useState(null);
  const generation = useRef(0);

  useEffect(() => {
    const current = ++generation.current;
    let active = true;
    setEvents([]); setSelected(null); setNextCursor(null); setError('');
    if (!allowed) { setLoading(false); return undefined; }
    setLoading(true);
    api.get('/security/audit', { params: { ...filters, limit: 50, ...(cursor ? { cursor } : {}) } })
      .then(response => {
        if (!active || generation.current !== current) return;
        if (!Array.isArray(response.data?.events)) throw new Error('Invalid response');
        setEvents(response.data.events); setNextCursor(response.data.nextCursor || null);
      })
      .catch(failure => {
        if (!active || generation.current !== current) return;
        setError(failure.response?.status === 403 ? 'You do not have permission to view security audit events.' :
          failure.response?.status === 401 ? 'Your session has expired. Sign in again.' :
          failure.response?.status === 400 ? 'The audit filters or history position are invalid. Refresh to start again.' :
          'Security audit events could not be loaded. Try Refresh or contact your system administrator.');
      })
      .finally(() => { if (active && generation.current === current) setLoading(false); });
    return () => { active = false; };
  }, [allowed, user?._id, filters, cursor, refresh]);

  if (!allowed) return <Alert severity="error">Security audit access is restricted to DycoVue administrators.</Alert>;
  const reset = () => { setCursor(null); setPrevious([]); setRefresh(value => value + 1); };
  const apply = event => {
    event.preventDefault();
    if (draft.from && draft.to && draft.from > draft.to) { setFilterError('The start date must be on or before the end date.'); return; }
    setFilterError(''); setFilters({ ...draft }); reset();
  };
  const detail = selected ? [
    ['Timestamp', timestamp(selected.timestamp)], ['Event ID', selected.id], ['Request ID', selected.requestId],
    ['Phase', selected.phase], ['Outcome', selected.outcome], ['HTTP status', selected.status],
    ['Category', selected.category], ['Action', selected.action], ['Method', selected.method],
    ['Route template', selected.route], ['Account reference', selected.actorTag],
    ['Facility reference', selected.facilityTag], ['Resource reference', selected.resourceTag], ['Signing key ID', selected.keyId],
  ] : [];

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ShieldOutlined color="primary" />
        <Typography variant="h4" component="h1">Security Audit</Typography>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Review recorded access and changes. Account and facility references are pseudonymized.
      </Typography>
      <Paper component="form" onSubmit={apply} sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField label="From (UTC)" type="date" value={draft.from} InputLabelProps={{ shrink: true }}
            onChange={event => setDraft(value => ({ ...value, from: event.target.value }))} />
          <TextField label="To (UTC)" type="date" value={draft.to} InputLabelProps={{ shrink: true }}
            onChange={event => setDraft(value => ({ ...value, to: event.target.value }))} />
          <TextField select label="Outcome" value={draft.outcome} sx={{ minWidth: 170 }}
            onChange={event => setDraft(value => ({ ...value, outcome: event.target.value }))}>
            <MenuItem value="">All outcomes</MenuItem>
            <MenuItem value="success">Success</MenuItem><MenuItem value="denied">Denied</MenuItem>
            <MenuItem value="error">Error</MenuItem><MenuItem value="pending">Pending</MenuItem>
          </TextField>
          <Button type="submit" variant="contained" disabled={loading}>Apply filters</Button>
          <Button startIcon={<Refresh />} onClick={reset} disabled={loading}>Refresh</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Date filters use UTC. Event times use your browser's time zone. Each request can have a start and completion record.
        </Typography>
        {filterError && <Alert severity="error" sx={{ mt: 1 }}>{filterError}</Alert>}
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <Box role="status" sx={{ p: 4, textAlign: 'center' }}><CircularProgress aria-label="Loading security audit" /></Box> :
        !error && events.length === 0 ? <Alert severity="info">No security audit events match these filters.</Alert> :
          !error && <TableContainer component={Paper}>
            <Table size="small" aria-label="Security audit events">
              <TableHead><TableRow>
                {['Time', 'Action', 'Outcome', 'Phase', 'Account reference', 'Signature', 'Details'].map(label => <TableCell key={label}>{label}</TableCell>)}
              </TableRow></TableHead>
              <TableBody>{events.map((event, index) => <TableRow key={event.id || index}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{timestamp(event.timestamp)}</TableCell>
                <TableCell>{event.category || 'Unavailable'} / {event.action || 'Unavailable'}{event.method ? ` · ${event.method}` : ''}</TableCell>
                <TableCell><Chip size="small" label={event.outcome || 'Unavailable'}
                  color={event.outcome === 'error' ? 'error' : event.outcome === 'denied' ? 'warning' : 'default'} />{event.status ? ` ${event.status}` : ''}</TableCell>
                <TableCell>{event.phase || 'Unavailable'}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{short(event.actorTag)}</TableCell>
                <TableCell><Chip size="small" label={integrityLabels[event.integrity] || 'Unverified'} color={integrityColor(event.integrity)} /></TableCell>
                <TableCell><Button size="small" onClick={() => setSelected(event)} aria-label={`View event ${event.id}`}>View</Button></TableCell>
              </TableRow>)}</TableBody>
            </Table>
          </TableContainer>}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Typography variant="body2">Page {previous.length + 1} · {events.length} records</Typography>
        <Stack direction="row" spacing={1}>
          <Button disabled={loading || previous.length === 0} onClick={() => {
            setCursor(previous[previous.length - 1]); setPrevious(value => value.slice(0, -1));
          }}>Newer</Button>
          <Button disabled={loading || !nextCursor} onClick={() => {
            setPrevious(value => [...value, cursor]); setCursor(nextCursor);
          }}>Older</Button>
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        Signature verification checks individual records. It does not establish that every event was recorded or that no history was removed.
      </Typography>
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>Security audit event</DialogTitle>
        <DialogContent dividers>
          {selected && <Alert severity={integrityColor(selected.integrity)} sx={{ mb: 2 }}>{integrityLabels[selected.integrity] || 'Unverified'}</Alert>}
          <Box component="dl" sx={{ m: 0 }}>{detail.map(([label, value]) => <Box key={label} sx={{ mb: 1.5 }}>
            <Typography component="dt" variant="body2" color="text.secondary">{label}</Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere', userSelect: 'text' }}>{value ?? 'Unavailable'}</Typography>
          </Box>)}</Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setSelected(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
